import React, { useState, useEffect } from "react";
import axios from "axios";
import Modal from "react-modal";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ErrorModal from "../../Security/ErrorModal"; // Use ErrorModal for both success and error messages
import { useSelector } from "react-redux";
import { Endpoints } from "../../../Global/Endpoints";
import { useTranslation } from 'react-i18next';

// Set app element for accessibility (required by react-modal)
Modal.setAppElement("#root");

const ExportModal = ({ isOpen, onClose, trackerType }) => {
  const { t, i18n } = useTranslation();
  const today = new Date();
  const oneMonthAgo = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
  const email = useSelector((state) => state.email.email);
  const token = localStorage.getItem("AccessToken");
  const [startDate, setStartDate] = useState(oneMonthAgo);
  const [endDate, setEndDate] = useState(today);
  const [showErrorModal, setShowErrorModal] = useState(false); // State to control ErrorModal
  const [modalMessage, setModalMessage] = useState(""); // Message for the ErrorModal
  const [isLoading, setIsLoading] = useState(false); // Loading state
  const [loading, setLoading] = useState(false);
  
  const ssoData = useSelector((state) => state.sso.ssoUserData);
  const isHighmarkPortal = ssoData?.Portal === "Highmark";
  const deviceType = isHighmarkPortal ? "HighmarkPortal" : "PatientPortal";
  const handleExport = async () => {
    setIsLoading(true); // Start loading
    setLoading(true);
    const fromDate = startDate.toLocaleDateString("en-US");
    const toDate = endDate.toLocaleDateString("en-US");
    const payload = {
      fromDate,
      toDate,
      ToEmailID: email,
      Duration: "",
      EmailID: email,
      DeviceType: deviceType
    };

    try {
      const response = await axios.post(
        Endpoints.SendMyGraphs,
        `_Params=${encodeURIComponent(JSON.stringify(payload))}`,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Check for success condition
      if (response.data.Result === 1) {
        let message = "";
      
        switch (trackerType) {
          case "fertility":
            message = t("exportsuccesmessagefertility");
            break;
          case "menopause":
            message = t("exportsuccesmessagemeanopause");
            break;
          case "pregnancy":
            message = t("exportsuccesmessagepregnency");
            break;
          case "Andropause":
            message = t("exportsuccesmessageandropause");
            break;
          default:
            message = t("exportsuccessdefault"); // Add a default message if necessary
        }
      
        setModalMessage(message);
      }
      
      else {
        setModalMessage(t("exporterrormessage"));
      }
      setShowErrorModal(true); // Show the modal with the appropriate message
    } catch (error) {
      setModalMessage(t("exporterrormessage"));
      setShowErrorModal(true); // Show error modal on network or other errors
    } finally {
      setLoading(false);
      setIsLoading(false); // Stop loading
    }
  };

  const closeErrorModal = () => {
    setShowErrorModal(false); // Close the ErrorModal
    onClose();
  };

  // Prevent manual typing in DatePicker
  const disableKeyboardInput = (e) => {
    e.preventDefault();
  };

  const handleClose = () => {
    setStartDate(oneMonthAgo);
    setEndDate(today);
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      setStartDate(oneMonthAgo);
      setEndDate(today);
    }
  }, [isOpen]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onRequestClose={handleClose}
        className="tracker-modal-content-ex"
        overlayClassName="tracker-modal-overlay"
        shouldCloseOnOverlayClick={false}
      >
        <h2 className="mb-5">{t("ExportNotes")}</h2>
        <div className="model-form-wrapper">
          {loading && (
            <div className="loading-container">
              <div className="loading"></div>
              <h2 className="loading-text">{t("loading")}</h2>
            </div>
          )}
          <div className="mb-5 row align-items-center">
            <label htmlFor="startDate" className="col-sm-4 model-form-label">{t("StartDatelabel")}</label>
            <div className="col-sm-6">
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                dateFormat="MM/dd/yyyy"
                placeholderText={t("Selectstartdateplaceholder")}
                maxDate={today}
                className="custom-datepicker form-control win-form-control calendar-control mobile-view"
                onKeyDown={disableKeyboardInput}
                popperPlacement="bottom-start"
              />
            </div>
          </div>

          <div className="mb-5 row align-items-center">
            <label htmlFor="endDate" className="col-sm-4 model-form-label">{t("EndDatelabel")}</label>
            <div className="col-sm-6">
              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                dateFormat="MM/dd/yyyy"
                placeholderText={t("EndDateplaceholder")}
                maxDate={today}
                minDate={startDate}
                className="custom-datepicker form-control win-form-control calendar-control mobile-view"
                onKeyDown={disableKeyboardInput}
                popperPlacement="bottom-start"
              />
            </div>
          </div>
        </div>

        <div className="mt-5 row align-items-center">
          <div className="col-sm-4"></div>
          <div className="col-sm-8 d-flex flex-wrap gap-2 justify-content-sm-start justify-content-center">
            <button onClick={handleClose} className="win-btn-secondary model-popup-btn me-1 mb-3 mb-sm-0">
              {t("Cancel")}
            </button>
            <button 
              onClick={handleExport} 
              className="win-btn-primary model-popup-btn" 
              disabled={isLoading}
            >
              {isLoading ? t("Exportingbtn") : t("Exportbtn")} {/* Show loading text */}
            </button>
          </div>            
        </div>
      </Modal>

      {showErrorModal && (
        <ErrorModal
          show={showErrorModal}
          onClose={closeErrorModal}
          message={modalMessage}
          closeButton={true}
        />
      )}
    </>
  );
};

export default ExportModal;
